/** Postmark bounce diagnostics for revenue collection email tooltips. */

export function isBounceLikeDeliveryState(deliveryState: string): boolean {
  return (
    deliveryState.startsWith('bounce') ||
    deliveryState === 'delivery_failed' ||
    deliveryState === 'spam_complaint'
  );
}

function asPayloadRecord(payload: unknown): Record<string, unknown> {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }
  return {};
}

/** Human-readable multi-line tooltip from a Postmark Bounce webhook payload. */
export function formatPostmarkBouncePayload(payload: unknown): string {
  const body = asPayloadRecord(payload);
  const bounceType = String(body.Type ?? '').trim();
  const description = typeof body.Description === 'string' ? body.Description.trim() : '';
  const details = typeof body.Details === 'string' ? body.Details.trim() : '';
  const email = typeof body.Email === 'string' ? body.Email.trim() : '';

  const lines: string[] = [];
  if (bounceType) lines.push(bounceType.replace(/([a-z])([A-Z])/g, '$1 $2'));
  if (description) lines.push(description);
  if (details && details !== description) lines.push(details);
  if (email) lines.push(`Recipient: ${email}`);

  return lines.length > 0 ? lines.join('\n') : 'Bounce (no details from Postmark)';
}

/** Single-line detail stored on email_sends.last_event_detail after webhooks. */
export function formatPostmarkBounceStoredDetail(body: Record<string, unknown>): string {
  const bounceType = String(body.Type ?? '').trim();
  const description = typeof body.Description === 'string' ? body.Description.trim() : '';
  const details = typeof body.Details === 'string' ? body.Details.trim() : '';
  const email = typeof body.Email === 'string' ? body.Email.trim() : '';

  const parts: string[] = [];
  if (bounceType) parts.push(bounceType);
  if (description) parts.push(description);
  if (details && details !== description) parts.push(details);
  if (email) parts.push(`Recipient: ${email}`);

  if (parts.length > 0) return parts.join(' — ');
  const bouncedAt = typeof body.BouncedAt === 'string' ? body.BouncedAt : null;
  return bouncedAt ? `${bounceType || 'Bounce'} (${bouncedAt})` : bounceType || 'Bounce';
}

/** True when last_event_detail is only the legacy Type (+ optional timestamp). */
export function isLegacyMinimalBounceLastEventDetail(detail: string): boolean {
  const t = detail.trim();
  if (!t) return true;
  if (/^(HardBounce|SoftBounce|Transient|VaporBounce|BadEmailAddress|Unsubscribe|Bounce)(\s*\([^)]+\))?$/i.test(t)) {
    return true;
  }
  return false;
}

export function bounceDetailFromLastEventDetail(lastEventDetail: string | null | undefined): string | null {
  const t = lastEventDetail?.trim();
  if (!t || isLegacyMinimalBounceLastEventDetail(t)) return null;
  return t.includes('\n') ? t : t.replace(/ — /g, '\n');
}

export type PostmarkBounceEventRow = {
  postmark_message_id: string;
  payload: unknown;
  occurred_at: string | null;
};

/** Latest bounce event per Postmark message id. */
export function buildBounceDetailByMessageId(
  events: PostmarkBounceEventRow[],
): Map<string, string> {
  const best = new Map<string, { at: number; detail: string }>();
  for (const ev of events) {
    const id = ev.postmark_message_id?.trim();
    if (!id) continue;
    const at = ev.occurred_at ? new Date(ev.occurred_at).getTime() : 0;
    const prev = best.get(id);
    if (prev && prev.at >= at) continue;
    best.set(id, { at, detail: formatPostmarkBouncePayload(ev.payload) });
  }
  return new Map([...best.entries()].map(([id, v]) => [id, v.detail]));
}

export function resolveEmailSendBounceTooltip(params: {
  delivery_state: string;
  last_event_detail?: string | null;
  postmark_message_id?: string | null;
  bounceDetailByMessageId: Map<string, string>;
  fallbackLabel?: string;
}): string | null {
  if (!isBounceLikeDeliveryState(params.delivery_state)) return null;

  const msgId = params.postmark_message_id?.trim();
  if (msgId) {
    const fromEvent = params.bounceDetailByMessageId.get(msgId);
    if (fromEvent) return fromEvent;
  }

  const fromStored = bounceDetailFromLastEventDetail(params.last_event_detail);
  if (fromStored) return fromStored;

  return params.fallbackLabel ?? null;
}

export type EmailSendWithBounceFields = {
  email_kind: string;
  delivery_state: string;
  last_event_at: string | null;
  last_event_detail?: string | null;
  postmark_message_id?: string | null;
  bounce_tooltip?: string | null;
};

export type EmailBounceDetailContent = {
  emailKindLabel: string;
  detail: string;
  occurredAt: string | null;
};

export function bounceDetailForEmailSend(
  send: EmailSendWithBounceFields,
  emailKindLabel: string,
  fallbackStateLabel: (deliveryState: string) => string,
): EmailBounceDetailContent | null {
  if (!isBounceLikeDeliveryState(send.delivery_state)) return null;
  return {
    emailKindLabel,
    detail:
      send.bounce_tooltip?.trim() || fallbackStateLabel(send.delivery_state),
    occurredAt: send.last_event_at,
  };
}

/** Most recent bounce for a hotel row (MailX icon). */
export function latestHotelBounceDetail(
  sends: EmailSendWithBounceFields[],
  hotelStatus: string,
  kindLabel: (emailKind: string) => string,
  fallbackStateLabel: (deliveryState: string) => string,
): EmailBounceDetailContent | null {
  const bounced = sends.filter((s) => isBounceLikeDeliveryState(s.delivery_state));
  if (bounced.length === 0) {
    if (hotelStatus === 'bounced') {
      return {
        emailKindLabel: 'Outreach email',
        detail: 'Email delivery bounced (no detailed reason on file)',
        occurredAt: null,
      };
    }
    return null;
  }

  const latest = [...bounced].sort((a, b) => {
    const ta = a.last_event_at ? new Date(a.last_event_at).getTime() : 0;
    const tb = b.last_event_at ? new Date(b.last_event_at).getTime() : 0;
    return tb - ta;
  })[0];

  return bounceDetailForEmailSend(
    latest,
    kindLabel(latest.email_kind),
    fallbackStateLabel,
  );
}

/** @deprecated Prefer latestHotelBounceDetail + EmailBounceDetailModal */
export function latestHotelBounceIconTooltip(
  sends: EmailSendWithBounceFields[],
  hotelStatus: string,
  kindLabel: (emailKind: string) => string,
  fallbackStateLabel: (deliveryState: string) => string,
): string | null {
  const content = latestHotelBounceDetail(
    sends,
    hotelStatus,
    kindLabel,
    fallbackStateLabel,
  );
  if (!content) return null;
  return `${content.emailKindLabel}\n${content.detail}`;
}

export function enrichEmailSendsWithBounceTooltips<
  T extends {
    delivery_state: string;
    last_event_detail?: string | null;
    postmark_message_id?: string | null;
  },
>(
  sends: T[],
  bounceEvents: PostmarkBounceEventRow[],
  fallbackStateLabel: (deliveryState: string) => string,
): (T & { bounce_tooltip: string | null })[] {
  const byMsg = buildBounceDetailByMessageId(bounceEvents);
  return sends.map((s) => ({
    ...s,
    bounce_tooltip: resolveEmailSendBounceTooltip({
      delivery_state: s.delivery_state,
      last_event_detail: s.last_event_detail ?? null,
      postmark_message_id: s.postmark_message_id ?? null,
      bounceDetailByMessageId: byMsg,
      fallbackLabel: isBounceLikeDeliveryState(s.delivery_state)
        ? fallbackStateLabel(s.delivery_state)
        : null,
    }),
  }));
}
